import React, { useEffect, useMemo, useRef } from 'react'
import NumericKeypad from '../../../components/ui-components/NumericKeypad'
import CustomAmountInput from '../../../components/ui-components/CustomAmountInput'
import { useNavigate } from 'react-router-dom'
import { paymentPageConfig } from '../../../utils/mf-execution/paymentPageConfig'
import { InvestPaymentMethodTypes, PaymentPageType } from '../../../types/mf-execution.types'
import { useMFExecutionContext } from '../../../state-management/contexts/MFExecutionContext'
import { useBuyContext } from '../../../state-management/contexts/BuyContext'
import { ToggleChip } from '../../../components/execution/ToggleChip'
import { useMetaDataContext } from '../../../state-management/contexts/MetaDataContext'
import { BuyPaymentMethodsButton } from '../../../components/execution/buy/BuyPaymentMethodsButton'
import { AllocationAmountAdjustmentToast } from '../../../components/execution/AllocationAmountAdjustmentToast'
import { useToast } from '../../../state-management/contexts/ToastContext'
import { PaymentPageHeader } from '../../../components/execution/PaymentPageHeader'
import { useInputKeypad } from '../../../hooks/mf-execution/useInputkeypad'
import { useProcessingPartnerMaintenanceBottomSheet } from '../../../hooks/mf-execution/useProcessingPartnerMaintenanceBottomSheet'
import { useBottomSheet } from '../../../state-management/contexts/BottomSheetContext'
import { BuyAutoPayConfirmationBottomSheet } from '../../../components/BottomSheetComponents/mf-execution/buy/BuyAutoPayConfirmationBottomSheet'
import { getTopPadding, getBottomPadding } from '../../../utils/sessionStorage'
import { convertNumberToCurrency } from '../../../utils/utils'
import { getEffectiveMaxAmount, getEffectiveStepper } from '../../../utils/mf-execution/paymentAmount'
import { EventNames } from '../../../types/misc.types'
import { trackEvent } from '../../../utils/appBridge'
import { buildOrderOtpPaymentFields } from '../../../utils/mf-execution/paymentMethodSelection'
import { isUpiIntentsFlowEnabled } from '../../../utils/mf-execution/upiIntentsFeature'

enum ErrorType {
  MIN = 'min',
  MAX = 'max',
  STEPPER = 'stepper'
}

const BuyPaymentPage: React.FC = () => {
  const { mfExecutionData } = useMFExecutionContext()
  const {
    paymentMethods,
    bankAccountDetails,
    defaultPaymentMethodDetails,
    selectedPaymentMethod,
    handlePaymentMethodChange,
    amount,
    initialShownAmount,
    handleAmountChange,
    fromAdvisory,
    fromApp,
    amountAdjustmentReason,
    clearAmountAdjustmentReason,
    handleOTPTrigger,
    installedUpiApps,
    requestUpiIntents,
    upiIntentMaximumAllowedAmount,
  } = useBuyContext()
  const { openBottomSheet, closeBottomSheet, destroyBottomSheet } = useBottomSheet()

  const navigate = useNavigate()
  const { isin, data: metaData } = useMetaDataContext()
  const fundName = metaData?.fundDetails?.fundName ?? ''
  const trackingFund = isin && fundName ? { fundIsin: isin, fundName } : undefined
  const paymentType = PaymentPageType.BUY
  const { placeholder, errorHelpText } = paymentPageConfig[paymentType as PaymentPageType]

  useProcessingPartnerMaintenanceBottomSheet('BuyPayment')

  const inputRef = useRef<HTMLDivElement>(null)
  const { showToast } = useToast()

  const minAmount = useMemo(() => {
    return mfExecutionData?.buy?.minAmount ?? 0
  }, [mfExecutionData?.buy?.minAmount])

  const stepper = useMemo(() => {
    return getEffectiveStepper(mfExecutionData?.buy?.stepper)
  }, [mfExecutionData?.buy?.stepper])

  const maximumAllowedAmount = useMemo(() => {
    return getEffectiveMaxAmount(mfExecutionData?.buy?.maxAmount ?? 0, paymentMethods)
  }, [paymentMethods, mfExecutionData?.buy?.maxAmount])

  const {
    handleBackspaceClick,
    handleCursorChange,
    handleNumberClick,
    errorType,
    cursorPos,
    enteredAmount,
    triggerValidation
  } = useInputKeypad({
    minAmount: minAmount ?? 0,
    maxAmount: maximumAllowedAmount ?? 0,
    stepper,
    defaultAmount: amount
  })

  useEffect(() => {
    if (enteredAmount) {
      handleAmountChange(parseFloat(enteredAmount))
    } else {
      handleAmountChange(0)
    }
  }, [enteredAmount])

  const navigateToOtp = () => {
    navigate(`/mutual-fund/${isin}/payment/${PaymentPageType.BUY}/otp`)
  }

  const handleFinalAction = () => {
    const otpPaymentFields = buildOrderOtpPaymentFields(selectedPaymentMethod, isUpiIntentsFlowEnabled())

    handleOTPTrigger({
      paymentMethodId: selectedPaymentMethod?.paymentMethodId || '',
      paymentMethodType: otpPaymentFields.paymentMethodType,
      transactionType: 'BUY',
      isin: isin,
      amount: amount,
      ...(otpPaymentFields.upiPackageName ? { upiPackageName: otpPaymentFields.upiPackageName } : {}),
    })
  }

  const handlePayNowClick = (method: 'ADD_UPI' | 'PAY_NOW') => {
    trackEvent({
      eventName: EventNames.ButtonTap,
      payload: {
        ScreenName: 'One time Enter amount',
        buttonLabel: method === 'ADD_UPI' ? 'Add UPI' : 'Pay now',
        eventId: method === 'ADD_UPI' ? 'MFE_SELECT_AMOUNT_CTA_ADD_UPI' : 'MFE_SELECT_AMOUNT_CTA_PAY_NOW',
        ...(trackingFund ?? {})
      }
    })
    // Trigger validation before proceeding and check result
    const validationError = triggerValidation()

    // Check if there are validation errors
    if (validationError) {
      return
    }

    if (method === 'ADD_UPI') {
      navigate(`/mutual-fund/${isin}/payment/${PaymentPageType.BUY}/add-upi`)
      return
    }

    // Shown amount vs continued amount, only after validation passes
    trackEvent({
      eventName: EventNames.ButtonTap,
      payload: {
        ScreenName: 'One time Enter amount',
        buttonLabel: 'Pay now',
        eventId: 'MFE_SELECT_AMOUNT_CONTINUED',
        amount,
        responseName: initialShownAmount != null ? String(initialShownAmount) : '',
        ...(trackingFund ?? {})
      }
    })

    if (selectedPaymentMethod?.type === InvestPaymentMethodTypes.MANDATE) {
      openBottomSheet(
        <BuyAutoPayConfirmationBottomSheet
          onDismiss={() => {
            closeBottomSheet()
          }}
          ScreenName='BuyPaymentPage'
          amount={amount}
          displayAccNumber={selectedPaymentMethod?.displayAccNumber ?? ''}
          trackingFund={trackingFund}
          onPlaceOrder={() => {
            handleFinalAction()
            destroyBottomSheet()
            navigateToOtp()
          }}
        />
      )
    } else {
      handleFinalAction()
      navigateToOtp()
    }
  }

  const handleAddUPIClick = () => {
    trackEvent({
      eventName: EventNames.ButtonTap,
      payload: {
        ScreenName: 'One time Enter amount',
        buttonLabel: 'Proceed',
        eventId: 'MFE_SELECT_AMOUNT_CTA_ADD_UPI',
        ...(trackingFund ?? {})
      }
    })
    const validationError = triggerValidation()
    if (validationError) {
      return
    }
    navigate(`/mutual-fund/${isin}/payment/${PaymentPageType.BUY}/add-upi`)
  }

  const getErrorMessage = () => {
    if (!errorType) return null

    switch (errorType) {
      case ErrorType.MIN:
        return `Minimum ${errorHelpText} is ${convertNumberToCurrency({ amount: minAmount ?? 0, casing: 'LOWER' })}`
      case ErrorType.MAX:
        return `Maximum ${errorHelpText} is ${convertNumberToCurrency({ amount: maximumAllowedAmount ?? 0, casing: 'LOWER' })}`
      case ErrorType.STEPPER:
        return stepper
          ? `Amount must be in multiples of ${convertNumberToCurrency({ amount: stepper, casing: 'LOWER' })}`
          : 'Invalid amount'
      default:
        return null
    }
  }

  useEffect(() => {
    trackEvent({
      eventName: EventNames.ScreenLoaded,
      payload: {
        ScreenName: 'One time Enter amount',
        ...(trackingFund ?? {})
      }
    })
  }, [])

  return (
    <div
      style={{ paddingTop: `calc(${getTopPadding()}px + 16px)`, paddingBottom: `calc(${getBottomPadding()}px + 16px)` }}
      className='w-full h-screen flex flex-col justify-between bg-white px-5'
    >
      <div className='w-full'>
        <PaymentPageHeader />
      </div>

      <div className='w-full flex flex-col justify-center flex-1'>
        <div className='flex flex-col gap-2 w-full'>
          <div className='h-8'>
            <div className='flex gap-2'>
              <ToggleChip
                testLocator='MFE_SELECT_AMOUNT_CTA_SIP'
                isLocked={fromAdvisory}
                options={[
                  {
                    id: PaymentPageType.BUY,
                    label: 'One-time'
                  },
                  {
                    id: PaymentPageType.SIP,
                    label: 'SIP'
                  }
                ]}
                defaultSelectedId={PaymentPageType.BUY}
                onSelectionChange={(selectedId) => {
                  trackEvent({
                    eventName: EventNames.UserClicked,
                    payload: {
                      ScreenName: 'One time Enter amount',
                      eventId: 'MFE_SELECT_AMOUNT_CTA_SIP',
                      buttonLabel: selectedId === PaymentPageType.SIP ? 'SIP' : 'One-time',
                      ...(trackingFund ?? {})
                    }
                  })
                  if (mfExecutionData?.buttonDisabledStates?.sip?.isDisabled) {
                    showToast({ message: 'SIP is not available for this fund', toastToBeShownById: 'BuyPaymentPage' })
                    return
                  }
                  const amountParam = amount > 0 ? `amount=${amount}` : ''
                  const fromParam = fromApp ? 'from=APP' : ''
                  const fromAdvisoryParam = fromAdvisory ? 'fromAdvisory=true' : ''
                  const query = [amountParam, fromParam, fromAdvisoryParam].filter(Boolean).join('&')
                  navigate(`/mutual-fund/${isin}/payment/${selectedId}${query ? `?${query}` : ''}`, { replace: true })
                }}
              />
            </div>
          </div>
          <div className='h-[74px] flex items-center w-full animate-fade-150'>
            <CustomAmountInput
              value={amount ? `${amount}` : ''}
              cursorPos={cursorPos}
              onCursorChange={handleCursorChange}
              inputRef={inputRef}
              placeholder={placeholder ?? ''}
            />
          </div>

          <div className='h-10 w-full'>
            {errorType ? (
              <div className='text-[#E77464] text-[14px] font-[500] animate-fade-150'>{getErrorMessage()}</div>
            ) : null}
          </div>
        </div>
      </div>
      <div className='w-full h-max pt-2 pb-5'>
        <AllocationAmountAdjustmentToast
          amountAdjustmentReason={amountAdjustmentReason}
          amount={amount}
          toastToBeShownById='BuyPaymentPage'
          clearAmountAdjustmentReason={clearAmountAdjustmentReason}
        />
        <div className='w-full h-[70px]'>
          {paymentMethods && (
            <BuyPaymentMethodsButton
              amount={amount}
              isDisabled={!!errorType || !amount}
              handlePayNowClick={handlePayNowClick}
              selectedPaymentMethod={selectedPaymentMethod}
              handlePaymentMethodChange={handlePaymentMethodChange}
              paymentMethods={paymentMethods}
              trackingFund={trackingFund}
              handleAddUPIClick={handleAddUPIClick}
              installedUpiApps={installedUpiApps}
              bankAccountDetails={bankAccountDetails}
              defaultPaymentMethodDetails={defaultPaymentMethodDetails}
              requestUpiIntents={requestUpiIntents}
              upiIntentMaximumAllowedAmount={upiIntentMaximumAllowedAmount}
            />
          )}
        </div>
      </div>
      <div className='w-full px-5'>
        <NumericKeypad onNumberClick={handleNumberClick} onBackspaceClick={handleBackspaceClick} />
      </div>
    </div>
  )
}

export default BuyPaymentPage
